import React from 'react';

interface CardInfoRowProps {
  title?: string;
  children: React.ReactNode;
  layout?: 'grid' | 'flex' | 'block';
  gridColumns?: number;
  gap?: string;
  padding?: string;
  marginBottom?: string;
  backgroundColor?: string;
  borderRadius?: string;
  styles?: React.CSSProperties;
  titleStyles?: React.CSSProperties;
  titlePosition?: 'top' | 'left' | 'none';
  alignItems?: 'flex-start' | 'center' | 'flex-end' | 'stretch';
  justifyContent?:
    | 'flex-start'
    | 'center'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
}

export const CardInfoRow: React.FC<CardInfoRowProps> = ({
  title,
  children,
  layout = 'block',
  gridColumns = 2,
  gap = '16px',
  padding = '6px 0',
  marginBottom = '10px',
  backgroundColor = '#f8f9fa',
  borderRadius = '12px',
  styles = {},
  titleStyles = {},
  titlePosition = 'top',
  alignItems = 'flex-start',
  justifyContent = 'flex-start',
}) => {
  const getLayoutStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      alignItems,
      justifyContent,
    };

    switch (layout) {
      case 'grid':
        return {
          ...baseStyles,
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gap,
        };
      case 'flex':
        return {
          ...baseStyles,
          display: 'flex',
          flexWrap: 'wrap',
          gap,
        };
      default:
        return baseStyles;
    }
  };

  const defaultTitleStyles: React.CSSProperties = {
    margin: titlePosition === 'left' ? '0 16px 0 0' : '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    ...titleStyles,
  };

  const defaultContainerStyles: React.CSSProperties = {
    marginBottom,
    padding,
    backgroundColor,
    borderRadius,
    ...styles,
  };

  if (titlePosition === 'left') {
    return (
      <div style={defaultContainerStyles}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          {title && <h3 style={defaultTitleStyles}>{title}</h3>}
          <div style={getLayoutStyles()}>{children}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={defaultContainerStyles}>
      {title && titlePosition === 'top' && (
        <h3 style={defaultTitleStyles}>{title}</h3>
      )}
      <div style={getLayoutStyles()}>{children}</div>
    </div>
  );
};

// Convenience components for common use cases
export const CardInfoGrid: React.FC<
  Omit<CardInfoRowProps, 'layout'> & { columns?: number }
> = ({ columns = 2, ...props }) => (
  <CardInfoRow {...props} layout="grid" gridColumns={columns} />
);

export const CardInfoFlex: React.FC<
  Omit<CardInfoRowProps, 'layout'> & { wrap?: boolean }
> = ({ wrap = true, ...props }) => (
  <CardInfoRow
    {...props}
    layout="flex"
    styles={{
      ...props.styles,
      flexWrap: wrap ? 'wrap' : 'nowrap',
    }}
  />
);

export const CardInfoBlock: React.FC<Omit<CardInfoRowProps, 'layout'>> = (
  props
) => <CardInfoRow {...props} layout="block" />;

// Specialized components for common patterns
export const CardInfoRowInline: React.FC<
  Omit<CardInfoRowProps, 'layout' | 'titlePosition'> & {
    titleWidth?: string;
    contentWidth?: string;
  }
> = ({ titleWidth = '200px', contentWidth = '1fr', ...props }) => (
  <CardInfoRow
    {...props}
    layout="grid"
    gridColumns={2}
    styles={{
      ...props.styles,
      gridTemplateColumns: `${titleWidth} ${contentWidth}`,
      alignItems: 'center',
    }}
    titlePosition="none"
  />
);

// Toggle button group component for common button toggle patterns
export const ToggleButtonGroup: React.FC<{
  title?: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  padding?: string;
  backgroundColor?: string;
  borderRadius?: string;
  gap?: string;
  buttonStyles?: React.CSSProperties;
  activeButtonStyles?: React.CSSProperties;
}> = ({
  title,
  options,
  value,
  onChange,
  padding = '16px',
  backgroundColor = '#f8f9fa',
  borderRadius = '12px',
  gap = '8px',
  buttonStyles = {},
  activeButtonStyles = {},
}) => (
  <div
    style={{
      padding,
      backgroundColor,
      borderRadius,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    }}
  >
    {title && (
      <h4
        style={{
          margin: 0,
          fontSize: '14px',
          fontWeight: '600',
          color: '#333',
        }}
      >
        {title}
      </h4>
    )}
    <div style={{ display: 'flex', gap }}>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            backgroundColor: value === option.value ? '#e3f2fd' : '#fff',
            color: value === option.value ? '#1976d2' : '#333',
            cursor: 'pointer',
            fontSize: '14px',
            ...buttonStyles,
            ...(value === option.value ? activeButtonStyles : {}),
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);
